import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import QueryString from "qs";
import {
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Check,
  Link2,
} from "lucide-react";
import { ReqMethods, ghl } from "../../../enums";
import { useAppServices } from "../../../hook/services";
import Spinner from "../../../components/ui/Spinner";
import env from "../../../config";

const STATUS = {
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
} as const;
type Status = (typeof STATUS)[keyof typeof STATUS];

// Visible progress for the install. Index advances as each phase completes.
const STEPS = [
  { key: "authorize", label: "Authorizing your account" },
  { key: "details", label: "Loading your workspace" },
  { key: "finish", label: "Finishing setup" },
] as const;

function GHL() {
  const { search } = useLocation();
  const code = new URLSearchParams(search).get("code");
  const AppServices = useAppServices();

  const [status, setStatus] = useState<Status>(STATUS.LOADING);
  const [step, setStep] = useState(0); // index into STEPS
  const [heading, setHeading] = useState("Setting things up");
  const [message, setMessage] = useState(
    "Hang tight while we connect your account…",
  );
  // Guard against React 18 StrictMode double-invoking the effect (code is single-use).
  const startedRef = useRef(false);

  const fail = (msg: string) => {
    setStatus(STATUS.ERROR);
    setHeading("Something went wrong");
    setMessage(msg);
  };

  const succeed = (msg: string) => {
    setStatus(STATUS.SUCCESS);
    setStep(STEPS.length);
    setHeading("You're all set");
    setMessage(msg);
  };

  // Exchange the redirect `code` for an access token (proxied via our backend
  // to avoid CORS / keep the request server-shaped).
  const exchangeCode = async () => {
    const payload = {
      method: ReqMethods.POST,
      path: ghl.APIs.oauth,
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      payload: QueryString.stringify({
        client_id: env.GHL.CLIENT_ID,
        client_secret: env.GHL.CLIENT_SECRET,
        grant_type: "authorization_code",
        code,
        refresh_token: "",
      }),
    };
    const { response } = await AppServices.services.ghl.call_service({ payload });
    console.log("exchangeCode response", response);
    return response; // { access_token, locationId, companyId, … }
  };

  // Pull the full location record so we can store address/email/phone/etc.
  const fetchLocation = async (creds: any) => {
    const payload = {
      method: ReqMethods.GET,
      path: ghl.APIs.v2.location(creds.locationId),
      key: creds.access_token,
      refresh_token: creds.refresh_token,
    };
    const { response } = await AppServices.services.ghl.call_service({ payload });
    return response?.location || null;
  };

  // Location install: save creds (as `auth`) + location details on the Account.
  const installLocation = async (creds: any) => {
    setStep(1);
    const location = await fetchLocation(creds);
    if (!location) {
      return fail("Couldn't load your location details. Please retry.");
    }

    setStep(2);
    const { error } = await AppServices.auth.integrate_app({
      payload: { creds, location },
    });
    if (error) {
      return fail("Something went wrong while saving your credentials.");
    }
    succeed("Smart Links is connected to your location. You can close this window.");
  };

  // Agency/bulk install: hand the agency-level creds to the backend, which
  // fans out to every location under the company itself.
  const installAgency = async (creds: any) => {
    setStep(2);
    const { error } = await AppServices.auth.agency_install({
      payload: { creds },
    });
    if (error) {
      return fail("Something went wrong during the agency install.");
    }
    succeed("Smart Links is connected to your agency. You can close this window.");
  };

  const run = async () => {
    if (!code) {
      return fail("Missing authorization code in the redirect URL.");
    }

    setStep(0);
    const creds = await exchangeCode();
    if (!creds || !creds.access_token) {
      return fail("Couldn't complete authorization. Please try again.");
    }

    // locationId present -> location install, otherwise agency/bulk install.
    if (creds.locationId) {
      await installLocation(creds);
    } else {
      await installAgency(creds);
    }
  };

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    run().catch(() => fail("Something went wrong during integration."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-ink-50 text-ink-900 flex items-center justify-center p-6">
      <div className="relative w-full max-w-md bg-white border border-ink-200/70 rounded-2xl shadow-card overflow-hidden anim-in">
        <div className="absolute inset-0 bg-mesh-1 opacity-60 pointer-events-none" />

        <div className="relative px-8 py-10 text-center">
          <StatusIcon status={status} />

          <h1 className="font-display text-xl font-bold tracking-tight mt-5">
            {heading}
          </h1>
          <p className="text-sm text-ink-500 mt-1.5 leading-relaxed">
            {message}
          </p>

          {status !== STATUS.ERROR && (
            <ol className="mt-7 space-y-2.5 text-left">
              {STEPS.map((s, i) => {
                const done = i < step;
                const active = i === step && status === STATUS.LOADING;
                return (
                  <li key={s.key} className="flex items-center gap-3">
                    <span
                      className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                        done
                          ? "bg-emerald-100 text-emerald-700"
                          : active
                            ? "bg-brand-50 text-brand-600"
                            : "bg-ink-100 text-ink-300"
                      }`}
                    >
                      {done ? (
                        <Check size={11} strokeWidth={3} />
                      ) : active ? (
                        <Spinner size={12} className="text-brand-600" />
                      ) : (
                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      )}
                    </span>
                    <span
                      className={`text-sm ${
                        done
                          ? "text-ink-700"
                          : active
                            ? "text-ink-900 font-medium"
                            : "text-ink-400"
                      }`}
                    >
                      {s.label}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}

          <div className="mt-8 inline-flex items-center gap-1.5 text-[11px] font-medium text-ink-500 bg-ink-50 ring-1 ring-ink-200 rounded-md px-2 py-1">
            <Sparkles size={11} className="text-brand-500" />
            <span>Smart Links · marketplace</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const StatusIcon = ({ status }: { status: Status }) => {
  if (status === STATUS.SUCCESS) {
    return (
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 text-emerald-600 flex items-center justify-center mx-auto">
        <CheckCircle2 size={26} strokeWidth={2.25} />
      </div>
    );
  }
  if (status === STATUS.ERROR) {
    return (
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 text-amber-700 flex items-center justify-center mx-auto">
        <AlertTriangle size={24} />
      </div>
    );
  }
  return (
    <div className="relative w-14 h-14 rounded-2xl bg-brand-gradient text-white shadow-brand-glow flex items-center justify-center mx-auto">
      <Link2 size={24} strokeWidth={2.25} />
      <span className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm">
        <Spinner size={14} className="text-brand-600" />
      </span>
    </div>
  );
};

export default GHL;
